import {Module} from '@fantasmagames/slot/src/common/Module';
import {AutoplayMenuEvents} from '@fantasmagames/slot/src/modules/popupmenus/autoplaymenu/AutoplayMenuEvents';
import {StopAutoplayButtonEvents} from '@fantasmagames/slot/src/modules/keypad/stopautoplaybutton/StopAutoplayButtonEvents';
import {Timer} from '@fantasmagames/core/src/update/Timer';
import {ReelsEvents} from '@fantasmagames/slot/src/modules/reels/spin/ReelsEvents';
import {FlowModuleEvents} from '@fantasmagames/slot/src/modules/flow/FlowModuleEvents';
import {AutoplayHandlerEvents} from '@fantasmagames/slot/src/modules/autoplay/AutoplayHandlerEvents';
import {OrientationEvents} from '@fantasmagames/slot/src/modules/orientation/OrientationEvents';
import {FreeSpinsEvents} from '@fantasmagames/slot/src/modules/freespins/intro/FreeSpinsEvents';
import {AutoplayBasicState} from '@fantasmagames/slot/src/modules/autoplay/states/AutoplayBasicState';
import {AutoplayInProgressState} from '@fantasmagames/slot/src/modules/autoplay/states/AutoplayInProgressState';
import {AutoplayPausedState} from '@fantasmagames/slot/src/modules/autoplay/states/AutoplayPausedState';
import {MessagesAPI} from '@fantasmagames/slot/src/logic/relax/MessagesAPI';
import {AutoplayFreeSpinsState} from '@fantasmagames/slot/src/modules/autoplay/states/AutoplayFreeSpinsState';
import {MainMenuEvents} from '@fantasmagames/slot/src/modules/mainmenu/MainMenuEvents';

export interface IAutoplayValues {
	singleWinLimit: number;
	winLimit: number;
	lossLimit: number;
	stopAutoplayOnWin: boolean;
}

export interface IAutoplaySettings {
	enabled: boolean;
	spinCount: number[];
	spinDefault: number;
	untilStop: boolean;
	maxSpinsUntilStop: number;
	winLimitEnabled: boolean;
	winLimitsAmounts: number[];
	winLimitDefault: number;
	lossLimitEnabled: boolean;
	lossLimitDefault: number;
	lossLimitsAmounts: number[];
}

export interface IRegulatedMarketRequirements {
	lossLimitRequired: boolean;
	winLimitRequired: boolean;
	singleWinLimitRequired: boolean;
	maxSpinsRequired: boolean;
	maxSpins: number;
	autoplayEnabled: boolean;
}

export const UnregulatedMarketAutoplaySettings: IRegulatedMarketRequirements = {
	lossLimitRequired: false,
	winLimitRequired: false,
	singleWinLimitRequired: false,
	maxSpinsRequired: false,
	maxSpins: 500,
	autoplayEnabled: true,
};

export const UKRegulatedMarketAutoplaySettings: IRegulatedMarketRequirements = {
	lossLimitRequired: true,
	winLimitRequired: false,
	singleWinLimitRequired: false,
	maxSpinsRequired: true,
	maxSpins: 100,
	autoplayEnabled: true,
};

export class AutoplayHandlerModule extends Module<null> {
	public static readonly BASIC: string = 'basic';
	public static readonly IN_PROGRESS: string = 'inprogress';
	public static readonly PAUSED: string = 'paused';
	public static readonly IN_FREESPINS: string = 'in_freespins';

	protected static readonly BEFORE_SPIN_TIMER: number = 250;

	protected _requiredSettings: IRegulatedMarketRequirements;

	protected _currentTimer: Timer;
	protected _canSpin: boolean = true;
	protected _isWinBigWin: boolean = false;
	protected _isWaitingForSpinToFinish: boolean = false;
	protected _stopOnAutoplayWin: boolean;

	protected _autospinsLeft: number = 0;
	protected _startAutospins: number;
	protected _singleWinLimit: number;
	protected _winLimit: number = 0;
	protected _lossLimit: number = 0;
	protected _lossBalanceLimit: number;
	protected _totalAutoplayWin: number;
	protected _winAmount: number;

	protected _stopOnSingleWinLimit: boolean;
	protected _stopOnWinLimit: boolean;
	protected _stopOnLossLimit: boolean;

	protected _isWaitingForGameResume: boolean = true;
	protected _isWaitingForMainMenuClose: boolean = true;

	protected enterFirstState(): void {
		this.gotoState(AutoplayHandlerModule.BASIC);
	}

	protected setupStates(): void {
		this.addState(new AutoplayBasicState(this, AutoplayHandlerModule.BASIC));
		this.addState(new AutoplayInProgressState(this, AutoplayHandlerModule.IN_PROGRESS));
		this.addState(new AutoplayPausedState(this, AutoplayHandlerModule.PAUSED));
		this.addState(new AutoplayFreeSpinsState(this, AutoplayHandlerModule.IN_FREESPINS));
	}

	protected setupViews(): void {}

	protected setupEvents(): void {
		this.onCurrentStateHandle(AutoplayMenuEvents.ACCEPTED);
		this.onCurrentStateHandle(StopAutoplayButtonEvents.STOP_AUTOPLAY_BUTTON_CLICKED);

		this.onCurrentStateHandle(FlowModuleEvents.CAN_SPIN);
		this.onCurrentStateHandle(FlowModuleEvents.FREESPINS_WIN);
		this.onCurrentStateHandle(FlowModuleEvents.WON_BIG_WIN);
		this.onCurrentStateHandle(FlowModuleEvents.NO_WIN);
		this.onCurrentStateHandle(FlowModuleEvents.WON);

		this.on(MessagesAPI.AUTOPLAY_PAUSED, this.onAutoplayShouldBePaused);
		this.on(MessagesAPI.GAME_HARD_PAUSED, this.onAutoplayShouldBePaused);
		this.on(MessagesAPI.GAME_RESUMED, this.onAutoplayShouldBeResumed);

		this.on(MainMenuEvents.MAIN_MENU_OPEN, this.onMainMenuOpened);
		this.on(MainMenuEvents.MAIN_MENU_CLOSED, this.onMainMenuClosed);

		this.on(AutoplayMenuEvents.UPDATE_AUTOPLAY_VALUES, this.onAutoplayValuesUpdated);

		this.on(ReelsEvents.REELS_SPINNING_STARTED, this.onSpinStarted);

		this.on(FreeSpinsEvents.FREE_SPINS_INTRO_STARTED, this.onFreeSpinsStarted);

		this.on(OrientationEvents.ORIENTATION_CHANGED_TO_PORTRAIT, this.onOrientationChanged);
		this.on(OrientationEvents.ORIENTATION_CHANGED_TO_LANDSCAPE, this.onOrientationChanged);
	}

	public startAutoplay(spinsLeft: number): void {
		this._autospinsLeft = spinsLeft;
		this._startAutospins = spinsLeft;
		if (this._stopOnLossLimit) {
			if (this._lossBalanceLimit < 0) {
				this._lossBalanceLimit = 0;
			}
		}

		if (this._canSpin) {
			this._winAmount = 0;
			this.tryToStartAutoplay();
		} else {
			this._isWaitingForSpinToFinish = true;
		}
	}

	protected onAutoplayShouldBePaused(eventID: string): void {
		this._isWaitingForGameResume = false;

		this.currentState.handle(eventID);
	}

	protected onAutoplayShouldBeResumed(eventID: string): void {
		this._isWaitingForGameResume = true;

		this.currentState.handle(eventID);
	}

	protected onMainMenuOpened(eventID: string): void {
		this._isWaitingForMainMenuClose = false;

		this.currentState.handle(eventID);
	}

	protected onMainMenuClosed(eventID: string): void {
		this._isWaitingForMainMenuClose = true;

		this.currentState.handle(eventID);
	}

	protected isFreeSpinsStarted(): boolean {
		return this.logic.isFreeSpinsStarted;
	}

	protected onOrientationChanged(): void {
		this.stopAutoplayAndEmitRoundsFinished();
	}

	protected onFreeSpinsStarted(): void {
		this.stopAutoplayAndEmitRoundsFinished();
	}

	public bigWinUpdate(): void {
		this._isWinBigWin = true;

		this._totalAutoplayWin += this.logic.getWin();
		this._winAmount = this.logic.getWin();
	}

	protected onAutoplayValuesUpdated(eventID: string, autoplayValues: IAutoplayValues): void {
		if (autoplayValues.singleWinLimit > 0) {
			this._stopOnSingleWinLimit = true;
			this._singleWinLimit = autoplayValues.singleWinLimit;
		} else {
			this._singleWinLimit = null;
			this._stopOnSingleWinLimit = false;
		}

		if (autoplayValues.winLimit > 0) {
			this._stopOnWinLimit = true;
			this._winLimit = autoplayValues.winLimit;
		} else {
			this._stopOnWinLimit = false;
			this._winLimit = null;
		}

		if (autoplayValues.lossLimit > 0) {
			this._stopOnLossLimit = true;
			this._lossLimit = autoplayValues.lossLimit;
			this._lossBalanceLimit = this._logic.currentBalance - this._lossLimit;
		} else {
			this._stopOnLossLimit = false;
			this._lossLimit = null;
			this._lossBalanceLimit = 0;
		}

		this._stopOnAutoplayWin = autoplayValues.stopAutoplayOnWin;
		this._totalAutoplayWin = 0;
	}

	public winUpdate(): void {
		if (!this._isWinBigWin) {
			// the isWinBigWin check is done because gameflow is currently setup to emit BigWin -> CanSpin -> Win.
			this._totalAutoplayWin += this.logic.getWin();
			this._winAmount = this.logic.getWin();
		} else {
			this._isWinBigWin = false;
		}
	}

	public stopAutoplayOnReachingLimits(): void {
		if (this.shouldStopOnAutoplayLimits()) {
			if (this.shouldStopOnSingleWinLimit()) {
				this.eventManager.emit(AutoplayHandlerEvents.AUTOPLAY_STOPPED_ON_SINGLE_WIN_LIMIT);
			} else if (this.shouldStopOnTotalAutoplayWin()) {
				this.eventManager.emit(AutoplayHandlerEvents.AUTOPLAY_STOPPED_ON_WIN_LIMIT);
			} else if (this.shouldStopOnLossBalanceLimit()) {
				this.eventManager.emit(AutoplayHandlerEvents.AUTOPLAY_STOPPED_ON_LOSE_LIMIT);
			}

			this.stopAutoplayAndEmitRoundsFinished();
		}
	}

	protected stopAutoplayOnFreeSpinWin(): void {
		if (this.isFreeSpinsStarted()) {
			this.stopAutoplayAndEmitRoundsFinished();
		}
	}

	protected onSpinStarted(): void {
		this._canSpin = false;
	}

	public tryToStartSpin(): void {
		this._canSpin = true;
		if (this.shouldStopOnAutoplayLimits()) {
			this.stopAutoplayOnReachingLimits();
		} else if (this.isFreeSpinsStarted()) {
			this.stopAutoplayOnFreeSpinWin();
		} else {
			if (this._isWaitingForSpinToFinish) {
				this._isWaitingForSpinToFinish = false;
				this.tryToStartAutoplay();
			} else {
				this.tryToStartAutoplay();
			}
		}
	}

	public setCanSpin(canSpin: boolean): void {
		this._canSpin = canSpin;
	}

	public tryToStartAutoplay(): void {
		this.timeUtils.killTimer(this._currentTimer);
		this._currentTimer = this.timeUtils.createTimer(this.startAutoplaySpin.bind(this), AutoplayHandlerModule.BEFORE_SPIN_TIMER);
	}

	public stopAutoplayAndEmitRoundsFinished(): void {
		this.stopAutoplay();
		this.eventManager.emit(AutoplayHandlerEvents.AUTOPLAY_ROUNDS_FINISHED);
	}

	public stopAutoplay(): void {
		this.timeUtils.killTimer(this._currentTimer);
		this.eventManager.emit(AutoplayHandlerEvents.AUTOPLAY_STOPPED);

		this._isWaitingForSpinToFinish = false;
	}

	protected startAutoplaySpin(): void {
		this._autospinsLeft--;
		this.eventManager.emit(AutoplayHandlerEvents.START_AUTOPLAY_SPIN, this._autospinsLeft, this._startAutospins);
	}

	protected shouldStopOnAutoplayLimits(): boolean {
		return this.shouldStopOnAutoplayWinLimits() || this.shouldStopOnAutoplaySpinOrLossLimits() || this.shouldStopOnSingleWinLimit();
	}

	protected shouldStopOnAutoplayWinLimits(): boolean {
		return (this._stopOnAutoplayWin && this._winAmount > 0) || this.shouldStopOnSingleWinLimit() || this.shouldStopOnTotalAutoplayWin();
	}

	protected shouldStopOnAutoplaySpinOrLossLimits(): boolean {
		return this.shouldStopOnAutoplaySpinsLeft() || this.shouldStopOnLossBalanceLimit();
	}

	protected shouldStopOnAutoplaySpinsLeft(): boolean {
		return this._autospinsLeft <= 0;
	}

	protected shouldStopOnLossBalanceLimit(): boolean {
		if (this._stopOnLossLimit) {
			return this.logic.currentBalance <= this._lossBalanceLimit;
		}

		return false;
	}

	protected shouldStopOnTotalAutoplayWin(): boolean {
		if (this._stopOnWinLimit) {
			return this._winLimit > 0 && this._totalAutoplayWin >= this._winLimit;
		}

		return false;
	}

	protected shouldStopOnSingleWinLimit(): boolean {
		if (this._stopOnSingleWinLimit) {
			return this._singleWinLimit > 0 && this._singleWinLimit <= this._winAmount;
		}

		return false;
	}

	public get isWaitingForGameResume(): boolean {
		return this._isWaitingForGameResume;
	}

	public get isWaitingForMainMenuClose(): boolean {
		return this._isWaitingForMainMenuClose;
	}

	public get requiredSettings(): IRegulatedMarketRequirements {
		return this._requiredSettings;
	}

	public set autospinsLeft(value: number) {
		this._autospinsLeft = value;
	}

	public set startAutospins(value: number) {
		this._startAutospins = value;
	}

	public get canSpin(): boolean {
		return this._canSpin;
	}

	public set winAmount(value: number) {
		this._winAmount = value;
	}

	public set isWaitingForSpinToFinish(value: boolean) {
		this._isWaitingForSpinToFinish = value;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/autoplay/AutoplayHandlerModule.ts